import { api } from '../../lib/api.js';
import { confirmedText, leftText, listLink } from '../../lib/lists.js';
import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { unsubscribeToken } from '../../lib/notifications.js';

/**
 * <community-list-link> — where the public lists' email links land: confirming
 * an address, or leaving one list.
 *
 * Public: someone on a list has no account. The token in the link is the
 * authorisation, and names one entry and one action.
 *
 * It waits for a click rather than acting on load, like
 * <community-unsubscribe>. Mail security scanners open links, and some run the
 * page's script while they do; a page that acted on load would confirm people
 * who never signed up, or take people off lists they never asked to leave. A
 * mail client's own unsubscribe button does not come here — it POSTs to the
 * API directly (RFC 8058).
 */
export class CommunityListLink extends BaseComponent {
    static properties = {
        list: { type: String },
        entryId: { type: String },
        action: { type: String },
        _token: { state: true },
        _working: { state: true },
        _done: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        this.list = '';
        this.entryId = '';
        this.action = '';
        this._token = unsubscribeToken(window.location.search);
        this._working = false;
        /** @type {string} what the page says once the link has worked */
        this._done = '';
        this._error = '';
    }

    async _act() {
        const link = listLink(this.list, this.entryId, this.action);
        if (!link) return;
        this._working = true;
        this._error = '';
        const path = `${link.path}?token=${encodeURIComponent(this._token)}`;
        try {
            if (link.method === 'DELETE') {
                await api.delete(path);
                this._done = leftText(this.list);
            } else {
                const result = await api.post(path);
                this._done = confirmedText(result.lists ?? []);
            }
        } catch (err) {
            const status = /** @type {any} */ (err).status;
            if (status === 400) {
                this._error =
                    "This link isn't valid, or it has expired. Confirmation links work for 7 days; sign up again to get a new one.";
            } else if (status === 409) {
                this._error =
                    'Your sign-up changed after this email was sent, so this link no longer covers it. A newer confirmation email is on its way; use the link in that one.';
            } else {
                this._error = String(/** @type {any} */ (err).message ?? err);
            }
        } finally {
            this._working = false;
        }
    }

    _renderBody() {
        const link = listLink(this.list, this.entryId, this.action);
        if (!link || !this._token) {
            return html`<p class="mb-0">This page needs the link from a Dice Driven email.</p>`;
        }

        if (this._done) {
            return html`<p class="mb-0">${this._done}</p>`;
        }

        return html`
            <p>${link.prompt}</p>
            ${this._error ? html`<div class="alert alert-warning small">${this._error}</div>` : nothing}
            <button class="btn btn-primary" ?disabled=${this._working} @click=${this._act}>
                ${link.button}
            </button>
        `;
    }

    render() {
        const link = listLink(this.list, this.entryId, this.action);
        return html`
            <div class="container py-4" style="max-width: 36rem;">
                <div class="card shadow-sm border-0">
                    <div class="card-body p-4">
                        <h1 class="h4 mb-3">${link ? link.title : 'Dice Driven'}</h1>
                        ${this._renderBody()}
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('community-list-link', CommunityListLink);
